import React, { useMemo, useState } from "react";
import {
  MdThumbDown,
  MdThumbUp,
  MdOutlineThumbDownAlt,
  MdOutlineThumbUpAlt,
} from "react-icons/md";
import Modal from "react-modal";

import { CommunityMemberAvatar } from "@/client_app/components/CommunityMemberAvatar";
import { sendRatingFeedBack } from "@/client_app/features/timesheetApproval/components/services/FetchAPIService";

interface FeedbackModalProps {
  isLiked: boolean;
  positionId: string;
  profileImageUrl: string;
  fullName: string;
  closeModal: () => void;
  submitted: () => void;
}

Modal.setAppElement("body");

const tags = [
  { label: "Skills", value: "skills" },
  { label: "Communication", value: "communication" },
  { label: "Professionalism", value: "professionalism" },
  { label: "Role Suitability", value: "role_suitability" },
  { label: "Performance", value: "performance" },
];
export const FeedbackModal = ({
  isLiked,
  closeModal,
  submitted,
  fullName,
  positionId,
  profileImageUrl,
}: FeedbackModalProps): JSX.Element => {
  const customStyles = {
    overlay: {
      zIndex: 9999,
      backgroundColor: "rgba(9, 32, 36, 0.9)",
    },
    content: {
      background: "none",
      border: "none",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      padding: "0",
    },
  };
  const [selectedTags, setSelectedTags] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState();
  const [isReviewStep, setIsReviewStep] = useState(false);
  const handleOnChange = (event) => {
    setAdditionalNotes(event.target.value);
  };

  const toggleTag = (value) => {
    setSelectedTags((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };
  const submitFeedback = () => {
    setIsReviewStep(true);
  };
  const submitReview = () => {
    sendRatingFeedBack({
      positionId,
      isLiked,
      categories: selectedTags,
      review: additionalNotes,
      rehire: false,
    }).then();
    closeModal();
    submitted();
  };

  const feedbackContent = useMemo(
    () => (
      <div className='flex justify-center items-center flex-wrap gap-4'>
        <CommunityMemberAvatar
          profileImageUrl={profileImageUrl}
          name={fullName}
          size='SMALL'
        />
        <div className='font-bold'>
          How was your experience with {fullName} ?
        </div>
        <div className='flex pt-2 gap-2'>
          {isLiked ? (
            <>
              <MdThumbUp className='cursor-pointer bg-gray-100 rounded-full h-10 w-10 p-2 text-primary-800' />
              <MdOutlineThumbDownAlt className='cursor-pointer bg-gray-100 rounded-full h-10 w-10 p-2' />
            </>
          ) : (
            <>
              <MdOutlineThumbUpAlt className='cursor-pointer bg-gray-100 rounded-full h-10 w-10 p-2' />
              <MdThumbDown className='cursor-pointer bg-gray-100 rounded-full h-10 w-10 p-2 text-error-400' />
            </>
          )}
        </div>
        <div className='w-full'>Select all that apply:</div>
        <div>
          {tags.map(({ label, value }) => (
            <button
              key={value}
              className={`btn btn-sm rounded-full font-bold text-secondary-700 m-1 ${
                selectedTags.includes(value)
                  ? isLiked
                    ? "bg-primary-50"
                    : "bg-error-200"
                  : "bg-gray-100"
              }`}
              onClick={() => toggleTag(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className='flex justify-between w-full pt-2'>
          <button className='btn btn-sm btn-secondary' onClick={closeModal}>
            CANCEL
          </button>
          <button className='btn btn-sm btn-primary' onClick={submitFeedback}>
            SUBMIT
          </button>
        </div>
      </div>
    ),
    [
      profileImageUrl,
      fullName,
      isLiked,
      closeModal,
      toggleTag,
      selectedTags,
      submitFeedback,
    ]
  );

  const reviewContent = useMemo(
    () => (
      <div className='flex justify-center items-center flex-wrap gap-4'>
        <CommunityMemberAvatar
          profileImageUrl={profileImageUrl}
          name={fullName}
          size='SMALL'
        />
        <div className='font-bold'>
          Write a review for {fullName} (optional):
        </div>
        <div className='pt-2'>
          <textarea
            onChange={handleOnChange}
            cols={100}
            className='w-full p-3 border-neutral-300 border rounded-lg'
          />
        </div>
        <div className='flex justify-between w-full pt-2'>
          <button className='btn btn-sm btn-secondary' onClick={submitReview}>
            SKIP
          </button>
          <button
            className='btn btn-sm btn-primary'
            disabled={!additionalNotes}
            onClick={submitReview}
          >
            SUBMIT
          </button>
        </div>
      </div>
    ),
    [profileImageUrl, fullName, closeModal, handleOnChange, submitReview]
  );

  return (
    <Modal isOpen={true} contentLabel='Feedback Modal' style={customStyles}>
      <div className='animated fadeIn fixed inset-0 overflow-y-auto flex items-center justify-center'>
        <div className='max-h-screen w-full max-w-lg relative'>
          <div className='bg-legacyColor-secondary-50 rounded-3xl border border-legacyColor-neutral-2-500 p-4 mx-auto max-w-md text-center'>
            <div className='p-8'>
              {isReviewStep ? reviewContent : feedbackContent}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
